import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { QUOTE_STATUSES, DISPATCH_STATUSES, statusMeta, dispatchMeta, formatMoney, relativeTime } from "@/lib/quotes";
import { toast } from "sonner";
import {
  ArrowLeft, User, Phone, MapPin, FileDown, Loader2, Pencil, Trash2, Truck, CalendarClock, Package, Share2,
} from "lucide-react";

export default function QuoteDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingStatus, setSavingStatus] = useState(false);
  const [statusNote, setStatusNote] = useState("");

  // Dispatch form state
  const [dispatchStatus, setDispatchStatus] = useState("");
  const [dispatchDate, setDispatchDate] = useState("");
  const [dispatchNote, setDispatchNote] = useState("");
  const [savingDispatch, setSavingDispatch] = useState(false);

  const role = user?.role;
  const isSales = role === "sales_team";
  const isMgmt = role === "management" || role === "admin";
  const isDispatchTeam = role === "dispatch_team" || role === "admin";
  const isOwn = quote && user && quote.created_by === user.user_id;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/quotes/${id}`);
      setQuote(data);
      if (data.dispatch) {
        setDispatchStatus(data.dispatch.status || "");
        setDispatchDate(data.dispatch.dispatch_date || "");
      }
    } catch (e) { toast.error(formatApiError(e)); navigate("/quotes"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const setStatus = async (newStatus) => {
    setSavingStatus(true);
    try {
      const { data } = await api.patch(`/quotes/${id}/status`, { status: newStatus, note: statusNote.trim() || null });
      setQuote(data);
      setStatusNote("");
      toast.success(`Status set to ${statusMeta(newStatus).label}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingStatus(false); }
  };

  const saveDispatch = async () => {
    if (!dispatchStatus) { toast.error("Pick a dispatch status"); return; }
    setSavingDispatch(true);
    try {
      const { data } = await api.patch(`/quotes/${id}/dispatch`, {
        status: dispatchStatus,
        dispatch_date: dispatchDate || null,
        note: dispatchNote.trim() || null,
      });
      setQuote(data);
      setDispatchNote("");
      toast.success("Dispatch updated");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingDispatch(false); }
  };

  const remove = async () => {
    try {
      await api.delete(`/quotes/${id}`);
      toast.success("Quotation removed");
      navigate("/quotes");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadPdf = async () => {
    try {
      const res = await fetch(`${API}/quotes/${id}/pdf`, { credentials: "include" });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${quote.quote_no}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Couldn't download PDF"); }
  };

  const STORE_MANAGER_WA = "919982369982";
  const shareOnWhatsApp = () => {
    if (!quote) return;
    const itemsSummary = (quote.items || [])
      .slice(0, 8)
      .map((it) => `• ${it.description} — Qty ${it.quantity} @ ${formatMoney(it.unit_price)}`)
      .join("\n");
    const more = (quote.items?.length || 0) > 8 ? `\n… and ${quote.items.length - 8} more` : "";
    const lines = [
      `*Quotation ${quote.quote_no}*`,
      `Customer: ${quote.customer?.name || ""}`,
      `Phone: ${quote.customer?.phone || ""}`,
      quote.customer?.address ? `Address: ${quote.customer.address}` : null,
      "",
      itemsSummary + more,
      "",
      `Subtotal: ${formatMoney(quote.subtotal)}`,
      quote.discount > 0 ? `Discount: -${formatMoney(quote.discount)}` : null,
      `*Final: ${formatMoney(quote.final_price)}*`,
      quote.note ? `\nNote: ${quote.note}` : null,
    ].filter(Boolean).join("\n");
    const url = `https://wa.me/${STORE_MANAGER_WA}?text=${encodeURIComponent(lines)}`;
    window.open(url, "_blank", "noopener");
  };

  if (loading || !quote) {
    return (
      <div className="p-6 text-center text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin inline-block mr-2" /> Loading…
      </div>
    );
  }

  const s = statusMeta(quote.status);
  const hidePrices = role === "dispatch_team";
  const canEdit = isSales && isOwn && quote.status === "sent";
  const canDelete = (isSales && isOwn && quote.status === "sent") || role === "admin";

  return (
    <div className="px-4 pt-4 pb-24 space-y-6" data-testid="quote-detail">
      <button
        onClick={() => navigate("/quotes")}
        className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-muted-foreground hover:text-foreground font-mono-mini"
        data-testid="back-to-list"
      >
        <ArrowLeft className="h-3 w-3" /> All quotations
      </button>

      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="font-mono-mini text-[11px] tabular-nums text-muted-foreground">{quote.quote_no}</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">{quote.customer?.name}</h1>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <span
              className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm"
              style={{ background: s.bg, color: s.color }}
              data-testid="quote-status-pill"
            >
              {s.label}
            </span>
            {quote.dispatch && (
              <span
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm border border-border"
                style={{ color: dispatchMeta(quote.dispatch.status).color }}
              >
                <Truck className="h-2.5 w-2.5" /> {dispatchMeta(quote.dispatch.status).label}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canEdit && (
            <Button variant="outline" size="sm" onClick={() => navigate(`/quotes/${id}/edit`)} data-testid="edit-quote-btn">
              <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit
            </Button>
          )}
          {(role === "sales_team" || role === "management" || role === "admin") && !hidePrices && (
            <Button
              size="sm"
              onClick={shareOnWhatsApp}
              data-testid="whatsapp-share-btn"
              className="text-white btn-lift"
              style={{ background: "#25D366" }}
            >
              <Share2 className="h-3.5 w-3.5 mr-1.5" /> WhatsApp
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={downloadPdf} data-testid="download-pdf-btn">
            <FileDown className="h-3.5 w-3.5 mr-1.5" /> PDF
          </Button>
          {canDelete && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="sm" className="text-destructive" data-testid="delete-quote-btn">
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Remove {quote.quote_no}?</AlertDialogTitle>
                  <AlertDialogDescription>This can't be undone.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={remove} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-quote">
                    Remove
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      {/* Customer */}
      <Card className="p-4 rounded-2xl border-border shadow-soft space-y-2">
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Customer</div>
        <div className="flex items-center gap-2 text-sm"><User className="h-3.5 w-3.5 text-muted-foreground" />{quote.customer?.name}</div>
        <div className="flex items-center gap-2 text-sm"><Phone className="h-3.5 w-3.5 text-muted-foreground" /><a href={`tel:${quote.customer?.phone}`} className="underline underline-offset-2">{quote.customer?.phone}</a></div>
        <div className="flex items-start gap-2 text-sm"><MapPin className="h-3.5 w-3.5 text-muted-foreground mt-0.5" />{quote.customer?.address}</div>
        <div className="text-[11px] text-muted-foreground pt-1 border-t border-border">
          Created by {quote.created_by_name || "—"} · {relativeTime(quote.created_at)}
        </div>
      </Card>

      {/* Items */}
      <div className="space-y-2" data-testid="items-view">
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Items</div>
        {quote.items?.map((it) => (
          <Card key={it.sr_no} className="p-3 rounded-2xl border-border shadow-soft">
            <div className="flex gap-3">
              {it.image_b64 ? (
                <img src={it.image_b64} alt="" className="h-20 w-20 rounded-md object-cover border border-border shrink-0" data-testid={`item-image-${it.sr_no}`} />
              ) : (
                <div className="h-20 w-20 rounded-md border border-dashed border-border grid place-items-center text-muted-foreground shrink-0">
                  <Package className="h-5 w-5" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-mono-mini text-[10px] uppercase tracking-widest text-muted-foreground">Sr {it.sr_no}</span>
                </div>
                <div className="mt-1 text-sm font-medium">{it.description}</div>
                <div className="mt-1 text-xs text-muted-foreground">
                  Qty <span className="tabular-nums font-mono-mini">{it.quantity}</span>
                  {!hidePrices && (
                    <>
                      {" · "}Unit <span className="tabular-nums font-mono-mini">{formatMoney(it.unit_price)}</span>
                    </>
                  )}
                </div>
                {!hidePrices && (
                  <div className="mt-1 text-sm font-medium text-right">
                    {formatMoney(it.total)}
                  </div>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Totals (hidden for dispatch) */}
      {!hidePrices && (
        <Card className="p-4 rounded-2xl border-border shadow-soft space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Subtotal</span>
            <span className="font-mono-mini tabular-nums">{formatMoney(quote.subtotal)}</span>
          </div>
          {quote.discount > 0 && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Discount</span>
              <span className="font-mono-mini tabular-nums text-destructive">- {formatMoney(quote.discount)}</span>
            </div>
          )}
          <div className="pt-2 border-t border-border flex items-center justify-between">
            <span className="text-sm font-medium">Final price</span>
            <span className="font-display text-2xl font-bold tabular-nums" style={{ color: s.color }}>
              {formatMoney(quote.final_price)}
            </span>
          </div>
        </Card>
      )}

      {quote.note && (
        <Card className="p-4 rounded-2xl border-border shadow-soft">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Note</div>
          <div className="text-sm mt-2 whitespace-pre-wrap">{quote.note}</div>
        </Card>
      )}

      {/* Management: status controls */}
      {isMgmt && (
        <Card className="p-4 rounded-2xl border-border shadow-soft space-y-3" data-testid="mgmt-status-panel">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Update status</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {QUOTE_STATUSES.map((sx) => (
              <Button
                key={sx.id}
                variant={quote.status === sx.id ? "default" : "outline"}
                onClick={() => setStatus(sx.id)}
                disabled={savingStatus || quote.status === sx.id}
                data-testid={`set-status-${sx.id}`}
                className="h-10 text-xs"
                style={quote.status === sx.id ? { background: sx.color, color: "white" } : undefined}
              >
                {sx.label}
              </Button>
            ))}
          </div>
          <Textarea
            data-testid="status-note"
            placeholder="Add a note (optional) — reason for lost, follow-up date, etc."
            value={statusNote}
            onChange={(e) => setStatusNote(e.target.value)}
            rows={2}
          />
          <p className="text-[11px] text-muted-foreground">Only management can change quotation status. Every change is logged.</p>
        </Card>
      )}

      {/* Dispatch: update panel */}
      {isDispatchTeam && quote.status === "won" && (
        <Card className="p-4 rounded-2xl border-border shadow-soft space-y-3" data-testid="dispatch-panel">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Dispatch</div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Status</Label>
            <Select value={dispatchStatus} onValueChange={setDispatchStatus}>
              <SelectTrigger data-testid="dispatch-status-select" className="mt-1.5 h-10">
                <SelectValue placeholder="Pick a status" />
              </SelectTrigger>
              <SelectContent>
                {DISPATCH_STATUSES.map((d) => (
                  <SelectItem key={d.id} value={d.id}>{d.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Dispatch date</Label>
            <Input
              type="date"
              data-testid="dispatch-date-input"
              value={dispatchDate}
              onChange={(e) => setDispatchDate(e.target.value)}
              className="mt-1.5 h-10 font-mono-mini"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Note</Label>
            <Textarea
              data-testid="dispatch-note"
              rows={2}
              value={dispatchNote}
              onChange={(e) => setDispatchNote(e.target.value)}
              placeholder="Courier, tracking number, packing details…"
              className="mt-1.5"
            />
          </div>
          <Button onClick={saveDispatch} disabled={savingDispatch} data-testid="dispatch-save-btn" className="w-full h-11 btn-lift">
            {savingDispatch ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Truck className="h-4 w-4 mr-2" />}
            Save dispatch update
          </Button>

          {quote.dispatch?.history?.length > 0 && (
            <div className="pt-3 border-t border-border">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">History</div>
              <ol className="space-y-2">
                {quote.dispatch.history.slice().reverse().slice(0, 6).map((h, i) => (
                  <li key={`${h.at}-${h.status}-${i}`} className="text-xs flex items-start gap-2">
                    <CalendarClock className="h-3 w-3 mt-0.5 text-muted-foreground shrink-0" />
                    <div className="min-w-0">
                      <div>
                        <span className="font-medium">{dispatchMeta(h.status).label}</span>
                        {h.by_name && <span className="text-muted-foreground"> · {h.by_name}</span>}
                      </div>
                      <div className="text-[10px] text-muted-foreground">{relativeTime(h.at)}</div>
                      {h.note && <div className="text-[11px] text-muted-foreground mt-0.5">{h.note}</div>}
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </Card>
      )}

      {/* Status history */}
      {quote.status_history?.length > 0 && !isDispatchTeam && (
        <Card className="p-4 rounded-2xl border-border shadow-soft" data-testid="status-history">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">Status history</div>
          <ol className="space-y-2">
            {quote.status_history.slice().reverse().map((h, i) => (
              <li key={`${h.at}-${h.status}-${i}`} className="text-xs flex items-start gap-2">
                <div className="h-1.5 w-1.5 rounded-full mt-1.5" style={{ background: statusMeta(h.status).color }} />
                <div>
                  <div>
                    <span className="font-medium">{statusMeta(h.status).label}</span>
                    {h.by_name && <span className="text-muted-foreground"> · {h.by_name}</span>}
                    <span className="text-muted-foreground"> · {relativeTime(h.at)}</span>
                  </div>
                  {h.note && <div className="text-[11px] text-muted-foreground mt-0.5">{h.note}</div>}
                </div>
              </li>
            ))}
          </ol>
        </Card>
      )}
    </div>
  );
}
